import {createStyles} from '@mantine/core';
import React, {useMemo} from 'react';

import {
  autoMapDataFrameColumns,
  AutoMapperColumnJoinType,
  AutoMapperMode,
} from 'analysis-tool/data-frame-creation/column-mapping/ColumnMappingUtil';
import DataFrameColumnAutoMapper from 'analysis-tool/data-frame-creation/column-mapping/DataFrameColumnAutoMapper';
import AnalysisViewSelector from 'analysis-tool/ui/components/AnalysisViewSelector';
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {STEP_TYPE_TO_USER_FACING_NAME} from 'graphql-client/__generated/GeneratedEnumMaps';
import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import StepConfigEditorPane from 'pipelines/generalized-step-config-editor/StepConfigEditorPane';
import {ConfigEditorStepType} from 'pipelines/generalized-step-config-editor/stepTypeIsConfigEditorSupported';
import {PipelineGraphQL, PipelineStepData, PipelineViewGraphQL} from 'pipelines/PipelineTypes';
import {getOutputDataForView} from 'pipelines/PipelineViewUtils';
import {validatePipelineStepConfig} from 'pipelines/step-config-validation/validatePipelineStepConfig';
import {getInputStepDatas} from 'pipelines/StepConnectionUtils';
import {JSONValue} from 'util/Types';

import RightSidebarSkeleton, {RightSidebarWidth} from '../components/RightSidebarSkeleton';
import makeFakePipelineStep from '../makeFakePipelineStep';
import {
  buildDataFramesInfos,
  buildUnionAutoMappedConfig,
  stepTypeHasRequiredInputs,
} from './ReplaceSourceUtils';

// ---------------------------------------------------------------------------
// Union auto-mapper subcomponent
// ---------------------------------------------------------------------------

function columnsToSystemNames(columns: Array<DataFrameColumnDef | null>): Array<string> {
  return columns.filter((c): c is DataFrameColumnDef => c != null).map((c) => c.systemName);
}

interface UnionAutoMapperProps {
  leftTable: PipelineViewGraphQL;
  rightTable: PipelineViewGraphQL;
  analysisSteps: PipelineGraphQL['steps'];
  draftConfig: Record<string, JSONValue>;
  setDraftConfig: (config: Record<string, JSONValue>) => void;
}

function UnionAutoMapper({
  leftTable,
  rightTable,
  analysisSteps,
  draftConfig,
  setDraftConfig,
}: UnionAutoMapperProps): JSX.Element {
  const leftColumns = useMemo(
    () => getOutputDataForView(leftTable, analysisSteps, PipelineStepDataKind.DATASET).columns,
    [leftTable, analysisSteps]
  );
  const rightColumns = useMemo(
    () => getOutputDataForView(rightTable, analysisSteps, PipelineStepDataKind.DATASET).columns,
    [rightTable, analysisSteps]
  );

  function handleAutoMapChange(
    templateColumns: Array<DataFrameColumnDef | null>,
    aligningColumns: Array<DataFrameColumnDef | null>
  ): void {
    setDraftConfig({
      ...draftConfig,
      primaryDataSourceColumns: columnsToSystemNames(templateColumns),
      secondaryDataSourcesColumns: [columnsToSystemNames(aligningColumns)],
    });
  }

  return (
    <DataFrameColumnAutoMapper
      templateColumns={leftColumns}
      aligningColumns={rightColumns}
      setSelectedTemplateColumns={(cols) => {
        // Build an auto-mapped config synchronously. DataFrameColumnAutoMapper calls both
        // setters in sequence — we derive the secondary from the auto-mapper rather than
        // waiting for a second callback.
        const {autoMappedAligningColumns} = autoMapDataFrameColumns(leftColumns, rightColumns, {
          mode: AutoMapperMode.NAME,
          columnJoinType: AutoMapperColumnJoinType.KEEP_SHARED,
        });
        handleAutoMapChange(cols, autoMappedAligningColumns);
      }}
      setSelectedAligningColumns={() => {
        // Handled by setSelectedTemplateColumns above — no-op to satisfy the interface.
      }}
      initialAutoMapperMode={AutoMapperMode.NAME}
      initialAutoMapperColumnJoinType={AutoMapperColumnJoinType.KEEP_SHARED}
      allowModeChange={true}
      allowColumnJoinTypeChange={false}
      autoMap={false}
    />
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = createStyles({
  inputSelectorsWrapper: {
    marginBottom: 16,
  },
  inputSelectorsRow: {
    display: 'flex',
    gap: 16,
  },
  inputSelector: {
    flex: 1,
    minWidth: 0,
  },
  inputSelectorLabel: {
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 4,
  },
  autoMapperWrapper: {
    marginTop: 20,
    marginBottom: 20,
  },
});

// ---------------------------------------------------------------------------
// ConfigurePhase component
// ---------------------------------------------------------------------------

export interface ConfigurePhaseState {
  selectedStepType: PipelineStepType;
  leftTable: PipelineViewGraphQL | null;
  rightTable: PipelineViewGraphQL | null;
}

interface ConfigurePhaseProps {
  state: ConfigurePhaseState;
  onSetTables: (leftTable: PipelineViewGraphQL | null, rightTable: PipelineViewGraphQL | null) => void;
  stepVersion: number;
  draftConfig: Record<string, JSONValue>;
  setDraftConfig: (config: Record<string, JSONValue>) => void;
  clientStateId: string;
  onBack: () => void;
  onNext: () => void;
  analysis: PipelineGraphQL;
}

export default function ConfigurePhase({
  state: {selectedStepType, leftTable, rightTable},
  onSetTables,
  stepVersion,
  draftConfig,
  setDraftConfig,
  clientStateId,
  onBack,
  onNext,
  analysis,
}: ConfigurePhaseProps): JSX.Element {
  const {classes} = useStyles();
  const inputDataShapes = getInputStepDatas(selectedStepType);
  const tables = [leftTable, rightTable];
  const fakeInputStepDatas: Array<PipelineStepData> = inputDataShapes.map((shape, index) => {
    const table = tables[index];
    const rawDatas = table ? [getOutputDataForView(table, analysis.steps, PipelineStepDataKind.DATASET)] : [];
    return {
      __typename: 'PipelineStepDataDomainNode' as const,
      id: `fake-${shape.systemName}`,
      name: shape.systemName,
      rawDatas,
    };
  });
  const draftStep = makeFakePipelineStep(selectedStepType, stepVersion, draftConfig, fakeInputStepDatas);
  const originalStep = makeFakePipelineStep(selectedStepType, stepVersion, null, fakeInputStepDatas);

  const hasRequiredInputs = stepTypeHasRequiredInputs(selectedStepType);
  const dataFramesInfos = hasRequiredInputs
    ? buildDataFramesInfos(selectedStepType, leftTable, rightTable, analysis.steps)
    : {};

  const isUnion = selectedStepType === PipelineStepType.UNION_DATASETS;
  const bothTablesSelected = leftTable != null && rightTable != null;

  const inputDataFrames = Object.fromEntries(
    Object.entries(dataFramesInfos).map(([name, infos]) => [name, infos.map((info) => info.dataFrameSpec)])
  );
  const {isComplete, errors} = validatePipelineStepConfig(
    selectedStepType,
    stepVersion,
    draftConfig,
    inputDataFrames
  );

  function handleUpdateTables(left: PipelineViewGraphQL | null, right: PipelineViewGraphQL | null): void {
    onSetTables(left, right);
    if (left && right) {
      const leftData = getOutputDataForView(left, analysis.steps, PipelineStepDataKind.DATASET);
      const rightData = getOutputDataForView(right, analysis.steps, PipelineStepDataKind.DATASET);
      const updates: Record<string, JSONValue> = {};
      // Auto-populate dataFrameName from table names when both are selected and user hasn't edited it
      if (!draftConfig.dataFrameName) {
        updates.dataFrameName = `${leftData.name} | ${rightData.name}`;
      }
      if (isUnion) {
        Object.assign(updates, buildUnionAutoMappedConfig(leftData.columns, rightData.columns));
      }
      if (Object.keys(updates).length > 0) {
        setDraftConfig({...draftConfig, ...updates});
      }
    }
  }

  const inputsSelected = !hasRequiredInputs || bothTablesSelected;
  const configValid = isComplete === true && errors.length === 0 && inputsSelected;

  return (
    <RightSidebarSkeleton
      title={STEP_TYPE_TO_USER_FACING_NAME[selectedStepType]}
      confirmationButtons={[
        {
          text: 'Back',
          onClick: onBack,
          dataTestAttrsElement: 'back',
        },
        {
          text: 'Next',
          onClick: onNext,
          dataTestAttrsElement: 'next',
          disabled: !configValid,
          isPrimary: true,
        },
      ]}
      width={RightSidebarWidth.WIDE}
      useAbsolutePositioning={true}
      // @ts-expect-error — intentionally fake PipelineStep; real step doesn't exist yet
      draftStep={draftStep}
      // @ts-expect-error — intentionally fake PipelineStep; real step doesn't exist yet
      originalStep={originalStep}
      clientStateId={clientStateId}
      dataTestAttrs={{
        component: 'ReplaceSourceStepRightSidebar',
        element: 'configure',
      }}
    >
      {hasRequiredInputs && (
        <div className={classes.inputSelectorsWrapper}>
          <div className={classes.inputSelectorsRow}>
            {inputDataShapes.map((shape, index) => (
              <div key={shape.systemName} className={classes.inputSelector}>
                <div className={classes.inputSelectorLabel}>{shape.displayName}</div>
                <AnalysisViewSelector
                  analysis={analysis}
                  topLevelOnly={false}
                  selectedView={index === 0 ? leftTable : rightTable}
                  onSelect={(view) => {
                    if (index === 0) {
                      handleUpdateTables(view, rightTable);
                    } else {
                      handleUpdateTables(leftTable, view);
                    }
                  }}
                  dataKind={PipelineStepDataKind.DATASET}
                  dataTestAttrs={{
                    component: 'ReplaceSourceStepRightSidebar',
                    element: `inputSelector-${shape.systemName}`,
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      )}
      {isUnion && leftTable != null && rightTable != null && (
        <div className={classes.autoMapperWrapper}>
          <UnionAutoMapper
            leftTable={leftTable}
            rightTable={rightTable}
            analysisSteps={analysis.steps}
            draftConfig={draftConfig}
            setDraftConfig={setDraftConfig}
          />
        </div>
      )}
      <StepConfigEditorPane
        stepType={selectedStepType as ConfigEditorStepType}
        stepVersion={stepVersion}
        config={draftConfig}
        setConfig={setDraftConfig}
        dataFramesInfos={dataFramesInfos}
        readOnly={false}
        clientStateId={clientStateId}
      />
    </RightSidebarSkeleton>
  );
}
